import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  enrichCompanyWithWebSearch, 
  getCompanyWebSearchData, 
  getCompanyWebSearchStatus,
  CompanyWebSearchResult,
  generateEmailTemplate,
  generateCallScript
} from '@/services/companyWebSearchService';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import MarkdownRenderer from '@/components/MarkdownRenderer';
import { Globe, RefreshCw, FileText, Phone, Copy, AlertCircle, AlertTriangle, Mail, MapPin, Star, Facebook, Linkedin, Twitter, Instagram, ExternalLink, Clock, CheckCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Company } from '@/lib/types';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';

const GOOGLE_PLACES_API_KEY = import.meta.env.VITE_GOOGLE_PLACES_API_KEY || '';

interface CompanyWebSearchPanelProps {
  company: Company;
  onRefresh?: () => void;
}

const CompanyWebSearchPanel: React.FC<CompanyWebSearchPanelProps> = ({ company, onRefresh }) => {
  const { toast } = useToast();
  const [webSearchData, setWebSearchData] = useState<CompanyWebSearchResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('summary');
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(null);
  const [webSearchStatus, setWebSearchStatus] = useState<string | null>(null);
  const [emailTemplate, setEmailTemplate] = useState<string | null>(null);
  const [callScript, setCallScript] = useState<string | null>(null);
  const [isGeneratingEmail, setIsGeneratingEmail] = useState(false);
  const [isGeneratingCall, setIsGeneratingCall] = useState(false);
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState<{type: 'email' | 'call', content: string} | null>(null);
  const [pendingDuration, setPendingDuration] = useState<number>(0);
  const [pendingStartTime, setPendingStartTime] = useState<Date | null>(null);
  const [showForceRefresh, setShowForceRefresh] = useState<boolean>(false);
  
  const STALLED_TIMEOUT = 60000; // 60 seconds before showing force refresh
  const companyIdRef = useRef(company.id);

  useEffect(() => {
    // Update ref and reset state when company changes
    companyIdRef.current = company.id;
    setActiveTab('summary');
    
    // Clear any existing intervals and reset state when company changes
    if (refreshInterval) {
      clearInterval(refreshInterval);
      setRefreshInterval(null);
    }
    
    // Reset state for the new company
    setWebSearchData(null);
    setWebSearchStatus(null);
    setPendingStartTime(null);
    setPendingDuration(0);
    setShowForceRefresh(false);
    
    // Fetch fresh data
    fetchWebSearchData();
    
    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [company.id]);

  useEffect(() => {
    let durationTimer: NodeJS.Timeout | null = null;
    
    if (webSearchStatus === 'pending' || webSearchStatus === 'processing') {
      if (!pendingStartTime) {
        setPendingStartTime(new Date());
      }
      
      durationTimer = setInterval(() => {
        if (pendingStartTime) {
          const elapsed = Date.now() - pendingStartTime.getTime();
          setPendingDuration(elapsed);
          
          if (elapsed >= STALLED_TIMEOUT && !showForceRefresh) {
            setShowForceRefresh(true);
          }
        }
      }, 1000);
    } else {
      setPendingStartTime(null);
      setPendingDuration(0);
      setShowForceRefresh(false);
    }
    
    return () => {
      if (durationTimer) {
        clearInterval(durationTimer);
      }
    };
  }, [webSearchStatus, pendingStartTime, showForceRefresh]);

  const fetchWebSearchData = async () => {
    const requestedId = company.id;
    setIsLoading(true);

    try {
      console.log(`[Fetch] Starting for company: ${requestedId}`);
      const status = await getCompanyWebSearchStatus(requestedId);
      
      if (companyIdRef.current !== requestedId) {
        console.log(`[Guard] Ignoring stale fetch result for ${requestedId}, current: ${companyIdRef.current}`);
        return;
      }
      
      if (!status) {
        console.log(`[Fetch] No status found for ${requestedId}, setting to not_started`);
        setWebSearchStatus('not_started');
        setIsLoading(false);
        return;
      }
      
      console.log(`[Fetch] Company ${requestedId} status: ${status.status}`);
      setWebSearchStatus(status.status);
      
      if (status.status === 'completed') {
        const data = await getCompanyWebSearchData(requestedId);
        
        if (companyIdRef.current !== requestedId) {
          console.log(`[Guard] Ignoring stale data for ${requestedId}, current: ${companyIdRef.current}`);
          return;
        }
        
        console.log(`[Set] Applying completed data for ${requestedId}:`, data ? 'Data found' : 'No data');
        setWebSearchData(data);
        if (refreshInterval) {
          clearInterval(refreshInterval);
          setRefreshInterval(null);
        }
      } 
      else if (status.status === 'pending' || status.status === 'processing') {
        console.log(`[Fetch] Company ${requestedId} is ${status.status}, starting polling`);
        if (!refreshInterval) {
          const interval = setInterval(checkStatus, 5000);
          setRefreshInterval(interval);
        }
      }
    } catch (error) {
      if (companyIdRef.current !== requestedId) {
        console.log(`[Guard] Ignoring error for stale request ${requestedId}`);
        return;
      }
      console.error(`[Error] Fetching web search data for company ${requestedId}:`, error);
      toast({
        title: 'Error',
        description: 'Failed to fetch web search data',
        variant: 'destructive',
      });
    } finally {
      if (companyIdRef.current === requestedId) {
        setIsLoading(false);
      }
    }
  };

  const checkStatus = async () => {
    const requestedId = company.id;
    
    try {
      console.log(`[Poll] Checking status for company: ${requestedId}`);
      const status = await getCompanyWebSearchStatus(requestedId);
      
      if (companyIdRef.current !== requestedId) {
        console.log(`[Guard] Ignoring stale poll result for ${requestedId}, current: ${companyIdRef.current}`);
        return;
      }
      
      if (!status) return;
      
      console.log(`[Poll] Status update for company ${requestedId}: ${status.status}`);
      setWebSearchStatus(status.status);
      
      if (status.status === 'completed') {
        const data = await getCompanyWebSearchData(requestedId);
        
        if (companyIdRef.current !== requestedId) {
          console.log(`[Guard] Ignoring stale completed data for ${requestedId}, current: ${companyIdRef.current}`);
          return;
        }
        
        console.log(`[Set] Applying completed poll data for ${requestedId}`);
        setWebSearchData(data);
        if (refreshInterval) {
          clearInterval(refreshInterval);
          setRefreshInterval(null);
        }
        if (onRefresh) {
          onRefresh();
        }
      } else if (status.status === 'failed') {
        if (companyIdRef.current !== requestedId) {
          console.log(`[Guard] Ignoring stale failed status for ${requestedId}`);
          return;
        }
        console.log(`[Poll] Failed enrichment for company ${requestedId}`);
        if (refreshInterval) {
          clearInterval(refreshInterval);
          setRefreshInterval(null);
        }
      }
    } catch (error) {
      if (companyIdRef.current === requestedId) {
        console.error(`[Error] Checking web search status for company ${requestedId}:`, error);
      }
    }
  };

  const handleRefresh = async (force: boolean = false) => {
    setIsRefreshing(true);
    
    try {
      await enrichCompanyWithWebSearch(company, true);
      setWebSearchStatus('pending');
      setPendingStartTime(new Date());
      setShowForceRefresh(false);
      
      if (!refreshInterval) {
        const interval = setInterval(checkStatus, 5000);
        setRefreshInterval(interval);
      }
      
      toast({
        description: force ? 'Force refresh initiated' : 'Web search refresh initiated',
      });
    } catch (error) {
      console.error('Error refreshing web search:', error);
      toast({
        title: 'Error',
        description: 'Failed to refresh web search data',
        variant: 'destructive',
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const formatDuration = (ms: number): string => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  const handleGenerateEmailTemplate = async () => {
    setIsGeneratingEmail(true);
    
    try {
      const template = await generateEmailTemplate(company.id);
      if (template) {
        setEmailTemplate(template);
        setCurrentTemplate({
          type: 'email',
          content: template
        });
        setIsTemplateDialogOpen(true);
      } else {
        toast({
          title: 'Error',
          description: 'Failed to generate email template',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error generating email template:', error);
      toast({
        title: 'Error',
        description: 'Failed to generate email template',
        variant: 'destructive',
      });
    } finally {
      setIsGeneratingEmail(false);
    }
  };

  const handleGenerateCallScript = async () => {
    setIsGeneratingCall(true);
    
    try {
      const script = await generateCallScript(company.id);
      if (script) {
        setCallScript(script);
        setCurrentTemplate({
          type: 'call',
          content: script
        });
        setIsTemplateDialogOpen(true);
      } else {
        toast({
          title: 'Error',
          description: 'Failed to generate call script',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error generating call script:', error);
      toast({
        title: 'Error',
        description: 'Failed to generate call script',
        variant: 'destructive',
      });
    } finally {
      setIsGeneratingCall(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      description: 'Copied to clipboard',
    });
  };

  const renderStatus = () => {
    if (isLoading) {
      return (
        <div className="flex items-center space-x-2">
          <Skeleton className="h-4 w-24" />
        </div>
      );
    }

    switch (webSearchStatus) {
      case 'completed':
        return <span className="text-green-600 font-medium">Completed</span>;
      case 'pending':
        return (
          <div className="flex items-center">
            <span className="text-amber-600 font-medium">Queued</span>
            {pendingDuration > 0 && (
              <span className="ml-2 text-xs text-muted-foreground">
                {formatDuration(pendingDuration)}
              </span>
            )}
          </div>
        );
      case 'processing':
        return (
          <div className="flex items-center">
            <span className="text-blue-600 font-medium">Processing</span>
            {pendingDuration > 0 && (
              <span className="ml-2 text-xs text-muted-foreground">
                {formatDuration(pendingDuration)}
              </span>
            )}
          </div>
        );
      case 'failed':
        return <span className="text-red-600 font-medium">Failed</span>;
      case 'not_started':
        return <span className="text-gray-600 font-medium">Not started</span>;
      default:
        return <span className="text-gray-600 font-medium">Unknown</span>;
    }
  };

  const renderEmptyState = () => {
    const isPending = webSearchStatus === 'pending' || webSearchStatus === 'processing';
    const buttonDisabled = isRefreshing || (isPending && !showForceRefresh);
    
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Globe className="h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium">No Web Search Data</h3>
        <p className="text-sm text-muted-foreground mt-2 mb-6 max-w-md">
          {webSearchStatus === 'failed' 
            ? 'The web search failed. Please try again.' 
            : 'Enrich this company with information from the web.'}
        </p>
        
        {showForceRefresh && isPending && (
          <Alert className="mb-6 max-w-md">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Search may be stalled</AlertTitle>
            <AlertDescription>
              The search has been pending for {formatDuration(pendingDuration)}. You can force a refresh.
            </AlertDescription>
          </Alert>
        )}
        
        <Button 
          onClick={() => handleRefresh(showForceRefresh)} 
          disabled={buttonDisabled}
        >
          {isRefreshing ? (
            <>
              <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              Refreshing...
            </>
          ) : showForceRefresh && isPending ? (
            <>
              <RefreshCw className="mr-2 h-4 w-4" />
              Force Refresh
            </>
          ) : (
            <>
              <Globe className="mr-2 h-4 w-4" />
              Start Web Search
            </>
          )}
        </Button>
      </div>
    );
  };

  const renderLoadingState = () => {
    return (
      <div className="space-y-4 p-4">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
      </div>
    );
  };

  const renderSourcesList = () => {
    if (!webSearchData || !webSearchData.sources || webSearchData.sources.length === 0) {
      return <p className="text-muted-foreground py-2">No sources available.</p>;
    }

    return (
      <div className="space-y-3">
        {webSearchData.sources.map((source, index) => (
          <div key={index} className="border rounded-md p-3">
            <a 
              href={source.url} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="font-medium text-blue-600 hover:underline"
            >
              {source.title || 'Untitled Source'}
            </a>
            <p className="text-sm text-muted-foreground mt-1 truncate">{source.url}</p>
          </div>
        ))}
      </div>
    );
  };

  const formatArrayItem = (item: any): string => {
    if (typeof item === 'string') {
      return item;
    } else if (typeof item === 'object' && item !== null) {
      if (item.name) return item.name;
      if (item.title) return item.title;
      if (item.text) return item.text;
      if (item.description) return item.description;
      
      try {
        return JSON.stringify(item);
      } catch (e) {
        return 'Unknown item';
      }
    }
    return String(item || 'N/A');
  };

  const convertArrayToMarkdown = (items: any[] | undefined, emptyMessage: string = "No data available."): string => {
    if (!items || items.length === 0) {
      return emptyMessage;
    }

    return items.map(item => `- ${formatArrayItem(item)}`).join('\n');
  };

  const renderArrayData = (items: any[] | undefined, emptyMessage: string = "No data available.") => {
    if (!items || items.length === 0) {
      return <p className="text-muted-foreground py-2">{emptyMessage}</p>;
    }

    try {
      return (
        <ul className="list-disc list-inside mt-1 space-y-1">
          {items.map((item, index) => (
            <li key={index} className="text-sm">{formatArrayItem(item)}</li>
          ))}
        </ul>
      );
    } catch (error) {
      console.error("Error rendering array data:", error);
      return <p className="text-red-500 py-2">Error displaying this data</p>;
    }
  };

  const renderContent = () => {
    if (isLoading) {
      return renderLoadingState();
    }

    if (!webSearchData) {
      return renderEmptyState();
    }

    return (
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-3 border-b mb-4">
          <TabsList className="w-full sm:w-auto overflow-x-auto scrollbar-hide snap-x snap-mandatory sm:snap-none">
            <TabsTrigger value="summary" className="px-2 py-1 text-xs sm:px-3 sm:py-1.5 sm:text-sm whitespace-nowrap">
              Summary
            </TabsTrigger>
            <TabsTrigger value="details" className="px-2 py-1 text-xs sm:px-3 sm:py-1.5 sm:text-sm whitespace-nowrap">
              Details
            </TabsTrigger>
            <TabsTrigger value="sources" className="px-2 py-1 text-xs sm:px-3 sm:py-1.5 sm:text-sm whitespace-nowrap">
              Sources
            </TabsTrigger>
          </TabsList>
          <div className="flex justify-end sm:justify-start flex-shrink-0">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => handleRefresh(false)} 
              disabled={isRefreshing || (webSearchStatus === 'pending' || webSearchStatus === 'processing') && !showForceRefresh}
              title={showForceRefresh ? "Force refresh" : "Refresh web search"}
              className="h-8 sm:h-9"
            >
              {isRefreshing ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        <TabsContent value="summary" className="mt-0">
          <ScrollArea className="h-[400px] pr-4">
            <div className="space-y-4">
              {(() => {
                const content =
                  (webSearchData.summary && webSearchData.summary.trim()) ||
                  (webSearchData.linkedIn?.companyDescription && webSearchData.linkedIn.companyDescription.trim()) ||
                  (webSearchData.rawText && webSearchData.rawText.trim()) ||
                  '';
                if (!content) {
                  return <p className="text-muted-foreground py-2">No summary available yet. Try refreshing or check Sources.</p>;
                }
                return <MarkdownRenderer content={content} />;
              })()}

              <div className="mt-6 space-y-3">
                <h3 className="font-medium">Generate Content</h3>
                <div className="flex flex-wrap gap-2">
                  <Button 
                    variant="outline" 
                    onClick={handleGenerateEmailTemplate}
                    disabled={isGeneratingEmail}
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    {isGeneratingEmail ? 'Generating...' : 'Email Template'}
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={handleGenerateCallScript}
                    disabled={isGeneratingCall}
                  >
                    <Phone className="h-4 w-4 mr-2" />
                    {isGeneratingCall ? 'Generating...' : 'Call Script'}
                  </Button>
                </div>
              </div>
            </div>
          </ScrollArea>
        </TabsContent>

        <TabsContent value="details" className="mt-0">
          <ScrollArea className="h-[400px] pr-4">
            {isLoading ? (
              <div className="space-y-6">
                {/* Loading Skeletons */}
                <div className="space-y-4">
                  <Skeleton className="h-8 w-3/4" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-2/3" />
                </div>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
                  <div key={i} className="space-y-2">
                    <Skeleton className="h-6 w-64" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-5/6" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-8">
                {/* Header Information - Business Information Section */}
                <div className="bg-primary/5 rounded-lg p-6 border border-primary/10">
                  <div className="flex items-start justify-between mb-4">
                    <h2 className="text-2xl font-bold">Business Information</h2>
                    {webSearchData && company.web_search_updated_at && (
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Clock className="h-3.5 w-3.5" />
                        <span>Updated {new Date(company.web_search_updated_at).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{company.name}</h3>
                <div className="space-y-2">
                  {(webSearchData.contactDetails?.address || webSearchData.places?.address) && (
                    <div className="flex items-start gap-2">
                      <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                      <span className="leading-relaxed">{webSearchData.contactDetails?.address || webSearchData.places?.address}</span>
                    </div>
                  )}
                  {(webSearchData.contactDetails?.website || company.website) && (
                    <div className="flex items-center gap-2">
                      <Globe className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <a 
                        href={webSearchData.contactDetails?.website || company.website} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="text-blue-600 hover:underline leading-relaxed"
                      >
                        {webSearchData.contactDetails?.website || company.website}
                      </a>
                    </div>
                  )}
                  {(webSearchData.contactDetails?.phone || webSearchData.places?.phone) && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <a 
                        href={`tel:${webSearchData.contactDetails?.phone || webSearchData.places?.phone}`}
                        className="text-blue-600 hover:underline leading-relaxed"
                      >
                        {webSearchData.contactDetails?.phone || webSearchData.places?.phone}
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Overview Section */}
              {(webSearchData.overview || webSearchData.summary || webSearchData.linkedIn?.companyDescription) && (
                <div className="bg-muted/30 rounded-lg p-5 border">
                  <h3 className="font-bold text-lg mb-3">Overview</h3>
                  <p className="leading-relaxed">
                    {webSearchData.overview || webSearchData.summary || webSearchData.linkedIn?.companyDescription}
                  </p>
                </div>
              )}

              {/* 1. Services, Products, and Business Offerings */}
              <div className="border-t pt-6">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-bold text-base">
                    <span className="text-primary text-lg mr-2">1.</span>
                    Services, Products, and Business Offerings
                  </h3>
                  {(webSearchData.services || webSearchData.products) && (
                    <Badge variant="secondary" className="text-[10px] gap-1 opacity-80">
                      <CheckCircle className="h-2.5 w-2.5" />
                      Official Website
                    </Badge>
                  )}
                </div>
                {(webSearchData.services && webSearchData.services.length > 0) || (webSearchData.products && webSearchData.products.length > 0) ? (
                  <div className="space-y-4">
                    {webSearchData.services && webSearchData.services.length > 0 && (
                      <div>
                        <p className="font-medium mb-2">Services:</p>
                        <div className="space-y-1.5 pl-1">
                          {webSearchData.services.map((service, idx) => (
                            <div key={idx} className="flex items-start gap-2">
                              <span className="text-primary mt-1.5">•</span>
                              <span className="leading-relaxed flex-1">{service}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {webSearchData.products && webSearchData.products.length > 0 && (
                      <div>
                        <p className="font-medium mb-2">Products:</p>
                        <div className="space-y-1.5 pl-1">
                          {webSearchData.products.map((product, idx) => (
                            <div key={idx} className="flex items-start gap-2">
                              <span className="text-primary mt-1.5">•</span>
                              <span className="leading-relaxed flex-1">{product}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-muted-foreground italic">Details not available</p>
                )}
              </div>

              {/* 2. History, Reputation, and Competitive Advantages */}
              <div className="border-t pt-6">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-bold text-base">
                    <span className="text-primary text-lg mr-2">2.</span>
                    History, Reputation, and Competitive Advantages
                  </h3>
                  {(webSearchData.history || webSearchData.reputation) && (
                    <Badge variant="secondary" className="text-[10px] gap-1 opacity-80">
                      <CheckCircle className="h-2.5 w-2.5" />
                      Official Website
                    </Badge>
                  )}
                </div>
                {(webSearchData.history || webSearchData.reputation || webSearchData.marketPosition) ? (
                  <div className="space-y-4">
                    {webSearchData.history && (
                      <div>
                        <p className="font-medium mb-2">History:</p>
                        <p className="leading-relaxed">{webSearchData.history}</p>
                      </div>
                    )}
                    {webSearchData.reputation && (
                      <div>
                        <p className="font-medium mb-2">Reputation:</p>
                        <p className="leading-relaxed">{webSearchData.reputation}</p>
                      </div>
                    )}
                    {webSearchData.marketPosition && (
                      <div>
                        <p className="font-medium mb-2">Market Position:</p>
                        <p className="leading-relaxed">{webSearchData.marketPosition}</p>
                      </div>
                    )}
                    {webSearchData.competitors && webSearchData.competitors.length > 0 && (
                      <div>
                        <p className="font-medium mb-2">Competitors:</p>
                        <div className="space-y-1.5 pl-1">
                          {webSearchData.competitors.map((competitor, idx) => (
                            <div key={idx} className="flex items-start gap-2">
                              <span className="text-primary mt-1.5">•</span>
                              <span className="leading-relaxed flex-1">{competitor}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-muted-foreground italic">Details not available</p>
                )}
              </div>

              {/* 3. Contact Information */}
              <div className="border-t pt-6">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-bold text-base">
                    <span className="text-primary text-lg mr-2">3.</span>
                    Contact Information
                  </h3>
                  {(webSearchData.contactDetails || webSearchData.places) && (
                    <Badge variant="secondary" className="text-[10px] gap-1 opacity-80">
                      <CheckCircle className="h-2.5 w-2.5" />
                      {webSearchData.places ? 'Google Places' : 'Official Website'}
                    </Badge>
                  )}
                </div>
                {(webSearchData.contactDetails || webSearchData.places) ? (
                  <div className="space-y-3">
                    {(webSearchData.contactDetails?.address || webSearchData.places?.address) && (
                      <div className="flex items-start gap-3">
                        <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium mb-0.5">Address</p>
                          <p className="leading-relaxed">{webSearchData.contactDetails?.address || webSearchData.places?.address}</p>
                        </div>
                      </div>
                    )}
                    {(webSearchData.contactDetails?.phone || webSearchData.places?.phone) && (
                      <div className="flex items-start gap-3">
                        <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <div>
                          <p className="font-medium mb-0.5">Call</p>
                          <a 
                            href={`tel:${webSearchData.contactDetails?.phone || webSearchData.places?.phone}`}
                            className="text-blue-600 hover:underline leading-relaxed"
                          >
                            {webSearchData.contactDetails?.phone || webSearchData.places?.phone}
                          </a>
                        </div>
                      </div>
                    )}
                    {webSearchData.contactDetails?.email && (
                      <div className="flex items-start gap-3">
                        <Mail className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <div>
                          <p className="font-medium mb-0.5">Email</p>
                          <a 
                            href={`mailto:${webSearchData.contactDetails.email}`}
                            className="text-blue-600 hover:underline leading-relaxed"
                          >
                            {webSearchData.contactDetails.email}
                          </a>
                        </div>
                      </div>
                    )}
                    {(webSearchData.contactDetails?.website || company.website) && (
                      <div className="flex items-start gap-3">
                        <Globe className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <div>
                          <p className="font-medium mb-0.5">Visit Website</p>
                          <a 
                            href={webSearchData.contactDetails?.website || company.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline flex items-center gap-1 leading-relaxed"
                          >
                            {webSearchData.contactDetails?.website || company.website}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-muted-foreground italic">Details not available</p>
                )}
              </div>

              {/* 4. Staff/Team Members */}
              <div className="border-t pt-6">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-bold text-base">
                    <span className="text-primary text-lg mr-2">4.</span>
                    Staff/Team Members
                  </h3>
                  {webSearchData.keyPeople && webSearchData.keyPeople.length > 0 && (
                    <Badge variant="secondary" className="text-[10px] gap-1 opacity-80">
                      <CheckCircle className="h-2.5 w-2.5" />
                      LinkedIn
                    </Badge>
                  )}
                </div>
                {webSearchData.keyPeople && webSearchData.keyPeople.length > 0 ? (
                  <div className="space-y-1.5 pl-1">
                    {webSearchData.keyPeople.map((person, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <span className="text-primary mt-1.5">•</span>
                        <span className="leading-relaxed flex-1">{person}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground italic">Details not available</p>
                )}
              </div>

              {/* 5. Locations */}
              <div className="border-t pt-6">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-bold text-base">
                    <span className="text-primary text-lg mr-2">5.</span>
                    Locations
                  </h3>
                  {(webSearchData.locations || webSearchData.contactDetails?.address || webSearchData.places?.address) && (
                    <Badge variant="secondary" className="text-[10px] gap-1 opacity-80">
                      <CheckCircle className="h-2.5 w-2.5" />
                      {webSearchData.places?.address ? 'Google Places' : 'Official Website'}
                    </Badge>
                  )}
                </div>
                {webSearchData.locations && webSearchData.locations.length > 0 ? (
                  <div className="space-y-1.5 pl-1 mb-4">
                    {webSearchData.locations.map((location, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <span className="text-primary mt-1.5">•</span>
                        <span className="leading-relaxed flex-1">{location}</span>
                      </div>
                    ))}
                  </div>
                ) : (webSearchData.contactDetails?.address || webSearchData.places?.address) ? (
                  <p className="leading-relaxed mb-4">{webSearchData.contactDetails?.address || webSearchData.places?.address}</p>
                ) : (
                  <p className="text-muted-foreground italic mb-4">Details not available</p>
                )}
                
                {/* Google Maps Embed */}
                {webSearchData.places?.placeId && GOOGLE_PLACES_API_KEY ? (
                  <div className="mt-3 rounded-lg overflow-hidden border-2 shadow-sm">
                    <iframe
                      width="100%"
                      height="300"
                      frameBorder="0"
                      style={{ border: 0 }}
                      src={`https://www.google.com/maps/embed/v1/place?key=${GOOGLE_PLACES_API_KEY}&q=place_id:${webSearchData.places.placeId}`}
                      allowFullScreen
                    ></iframe>
                  </div>
                ) : (webSearchData.places?.mapsUrl || (webSearchData.contactDetails?.address || webSearchData.places?.address)) && (
                  <a 
                    href={webSearchData.places?.mapsUrl || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(webSearchData.contactDetails?.address || webSearchData.places?.address || '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-blue-600 hover:underline"
                  >
                    <MapPin className="h-4 w-4" />
                    View on Google Maps
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>

              {/* 6. Social Media */}
              <div className="border-t pt-6">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-bold text-base">
                    <span className="text-primary text-lg mr-2">6.</span>
                    Social Media
                  </h3>
                  {webSearchData.socialMedia && Object.values(webSearchData.socialMedia).some(v => v) && (
                    <Badge variant="secondary" className="text-[10px] gap-1 opacity-80">
                      <CheckCircle className="h-2.5 w-2.5" />
                      Official Website
                    </Badge>
                  )}
                </div>
                {webSearchData.socialMedia && Object.values(webSearchData.socialMedia).some(v => v) ? (
                  <div className="flex flex-wrap gap-2">
                    {webSearchData.socialMedia.facebook && (
                      <a 
                        href={webSearchData.socialMedia.facebook} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-[#1877F2] text-white hover:opacity-90 transition-opacity text-sm"
                      >
                        <Facebook className="h-4 w-4" />
                        Facebook
                      </a>
                    )}
                    {webSearchData.socialMedia.linkedin && (
                      <a 
                        href={webSearchData.socialMedia.linkedin} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-[#0A66C2] text-white hover:opacity-90 transition-opacity text-sm"
                      >
                        <Linkedin className="h-4 w-4" />
                        LinkedIn
                      </a>
                    )}
                    {webSearchData.socialMedia.twitter && (
                      <a 
                        href={webSearchData.socialMedia.twitter} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-[#1DA1F2] text-white hover:opacity-90 transition-opacity text-sm"
                      >
                        <Twitter className="h-4 w-4" />
                        Twitter
                      </a>
                    )}
                    {webSearchData.socialMedia.instagram && (
                      <a 
                        href={webSearchData.socialMedia.instagram} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] text-white hover:opacity-90 transition-opacity text-sm"
                      >
                        <Instagram className="h-4 w-4" />
                        Instagram
                      </a>
                    )}
                  </div>
                ) : (
                  <p className="text-muted-foreground italic">Details not available</p>
                )}
              </div>

              {/* 7. Support Channels */}
              <div className="border-t pt-6">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-bold text-base">
                    <span className="text-primary text-lg mr-2">7.</span>
                    Support Channels
                  </h3>
                  {webSearchData.supportChannels && webSearchData.supportChannels.length > 0 && (
                    <Badge variant="secondary" className="text-[10px] gap-1 opacity-80">
                      <CheckCircle className="h-2.5 w-2.5" />
                      Official Website
                    </Badge>
                  )}
                </div>
                {webSearchData.supportChannels && webSearchData.supportChannels.length > 0 ? (
                  <div className="space-y-1.5 pl-1">
                    {webSearchData.supportChannels.map((channel, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <span className="text-primary mt-1.5">•</span>
                        <span className="leading-relaxed flex-1">{channel}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground italic">Details not available</p>
                )}
              </div>

              {/* 8. Recent Company News */}
              <div className="border-t pt-6">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-bold text-base">
                    <span className="text-primary text-lg mr-2">8.</span>
                    Recent Company News
                  </h3>
                  {webSearchData.recentNews && webSearchData.recentNews.length > 0 && (
                    <Badge variant="secondary" className="text-[10px] gap-1 opacity-80">
                      <CheckCircle className="h-2.5 w-2.5" />
                      Web Search
                    </Badge>
                  )}
                </div>
                {webSearchData.recentNews && webSearchData.recentNews.length > 0 ? (
                  <div className="space-y-3">
                    {webSearchData.recentNews.map((news, idx) => (
                      <div key={idx} className="border-l-2 border-primary pl-4 py-2">
                        <p className="leading-relaxed">{news}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground italic">Details not available</p>
                )}
              </div>

              {/* 9. Customer Reviews Summary */}
              <div className="border-t pt-6">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-bold text-base">
                    <span className="text-primary text-lg mr-2">9.</span>
                    Customer Reviews Summary
                  </h3>
                  {webSearchData.places?.reviews && webSearchData.places.reviews.length > 0 && (
                    <Badge variant="secondary" className="text-[10px] gap-1 opacity-80">
                      <CheckCircle className="h-2.5 w-2.5" />
                      Google Reviews
                    </Badge>
                  )}
                </div>
                {webSearchData.places?.reviews && webSearchData.places.reviews.length > 0 ? (
                  <div className="space-y-3">
                    {/* Overall Rating */}
                    {webSearchData.places.rating && (
                      <div className="flex items-center gap-3 pb-2 border-b">
                        <div className="flex items-center">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star 
                              key={star} 
                              className={`h-5 w-5 ${star <= Math.round(webSearchData.places?.rating || 0) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                            />
                          ))}
                        </div>
                        <span className="text-lg font-semibold">{webSearchData.places.rating.toFixed(1)}</span>
                        {webSearchData.places.reviewsCount && (
                          <span className="text-sm text-muted-foreground">({webSearchData.places.reviewsCount} reviews)</span>
                        )}
                      </div>
                    )}
                    
                    {/* Individual Reviews */}
                    <div className="space-y-3">
                      {webSearchData.places.reviews.slice(0, 3).map((review: any, idx: number) => (
                        <div key={idx} className="border rounded-md p-3 space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-sm">{review.author_name}</span>
                            <div className="flex items-center">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Star 
                                  key={star} 
                                  className={`h-3.5 w-3.5 ${star <= review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                                />
                              ))}
                            </div>
                          </div>
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {review.text?.substring(0, 250)}{review.text?.length > 250 ? '...' : ''}
                          </p>
                          {review.time && (
                            <p className="text-xs text-muted-foreground">
                              {new Date(review.time * 1000).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-muted-foreground italic">Details not available</p>
                )}
              </div>

                {/* Additional Company Info */}
                {(webSearchData.foundedYear || webSearchData.companySize || company.size) && (
                  <div className="pt-6 border-t">
                    <h3 className="font-bold text-base mb-3">
                      Company Information
                    </h3>
                  <div className="space-y-2">
                    {webSearchData.foundedYear && (
                      <p className="leading-relaxed"><span className="font-medium">Founded:</span> {webSearchData.foundedYear}</p>
                    )}
                    {(webSearchData.companySize || company.size) && (
                      <p className="leading-relaxed"><span className="font-medium">Company Size:</span> {webSearchData.companySize || company.size}</p>
                    )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </ScrollArea>
        </TabsContent>

        <TabsContent value="sources" className="mt-0">
          <ScrollArea className="h-[400px] pr-4">
            {renderSourcesList()}
          </ScrollArea>
        </TabsContent>
      </Tabs>
    );
  };

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex-1">
              <CardTitle className="text-lg sm:text-xl">Web Search</CardTitle>
              <CardDescription className="text-sm">AI-powered insights about the company</CardDescription>
            </div>
            <div className="flex-shrink-0">{renderStatus()}</div>
          </div>
        </CardHeader>
        <CardContent>
          {renderContent()}
        </CardContent>
      </Card>

      <Dialog open={isTemplateDialogOpen} onOpenChange={setIsTemplateDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {currentTemplate?.type === 'email' ? 'Email Template' : 'Call Script'}
            </DialogTitle>
            <DialogDescription>
              AI-generated {currentTemplate?.type === 'email' ? 'email template' : 'call script'} based on web search data
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="relative">
              <Textarea 
                value={currentTemplate?.content || ''} 
                readOnly 
                className="min-h-[300px] font-mono text-sm"
              />
              <Button 
                size="sm" 
                variant="ghost" 
                className="absolute top-2 right-2" 
                onClick={() => currentTemplate && copyToClipboard(currentTemplate.content)}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CompanyWebSearchPanel;
